import { useState, useEffect, useCallback } from "react";
import { useLocation } from "wouter";
import { FileText, ExternalLink, Building2, Loader2 } from "lucide-react";
import InvestorPortalDisclaimer from "@/components/investor-portal-disclaimer";

const BASE = import.meta.env.BASE_URL;

export default function InvestorPortalDocuments() {
  const [, navigate] = useLocation();
  const [loading, setLoading] = useState(true);
  const [docs, setDocs] = useState<any[]>([]);

  const fetchDocs = useCallback(async () => {
    const token = localStorage.getItem("investor_token");
    if (!token) { navigate("/investor/login"); return; }

    try {
      const res = await fetch(`${BASE}api/investor-portal/documents`, {
        headers: { "x-investor-token": token },
      });
      if (res.status === 401) { navigate("/investor/login"); return; }
      if (res.ok) setDocs(await res.json());
    } catch {}
    setLoading(false);
  }, [navigate]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-6 h-6 text-[#FF5C00] animate-spin" />
    </div>
  );

  const typeLabels: Record<string, string> = {
    agreement: "Agreement",
    share_transfer: "Share Transfer",
    rofr_agreement: "ROFR Agreement",
    tax: "Tax Document",
    report: "Report",
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold font-serif tracking-tight text-white flex items-center gap-2">
          <FileText className="w-6 h-6 text-[#FF5C00]" /> Documents
        </h1>
        <p className="text-white/40 text-sm mt-1">Your agreements and investment documents</p>
      </div>

      {docs.length === 0 ? (
        <div className="bg-white/[0.03] border border-white/10 rounded-xl p-12 text-center">
          <FileText className="w-8 h-8 text-white/20 mx-auto mb-3" />
          <p className="text-white/40">No documents uploaded yet</p>
          <p className="text-white/20 text-sm mt-1">Your agreements will appear here once uploaded by the Favcy team</p>
        </div>
      ) : (
        <div className="space-y-3">
          {docs.map((doc: any) => (
            <a
              key={doc.id}
              href={doc.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between bg-white/[0.03] border border-white/10 rounded-xl p-5 hover:bg-white/[0.05] hover:border-white/20 transition-all group"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white/30" />
                </div>
                <div>
                  <p className="text-white text-sm font-medium">{doc.title}</p>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-white/30 text-xs">{typeLabels[doc.type] || doc.type}</span>
                    {doc.companyName && (
                      <span className="text-white/20 text-xs flex items-center gap-1">
                        <Building2 className="w-3 h-3" /> {doc.companyName}
                      </span>
                    )}
                    <span className="text-white/20 text-xs">
                      {new Date(doc.uploadedAt).toLocaleDateString("en-IN")}
                    </span>
                  </div>
                </div>
              </div>
              <ExternalLink className="w-4 h-4 text-white/20 group-hover:text-[#FF5C00] transition-colors" />
            </a>
          ))}
        </div>
      )}

      <InvestorPortalDisclaimer />
    </div>
  );
}
